"use client"

import { useId, useState, type FormEvent } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { captureChatError } from "@/lib/sentry-chat"
import { getPostHogDistinctId } from "@/lib/insurance/insurance-tracking"

interface SlowReplyFormProps {
  /** Per-contact widget JWT (session.source_id) — required to identify. */
  authToken: string | null
  conversationId: number | null
  /** How long the visitor has been waiting, so the agent sees it on the note. */
  waitedMs: number
  onSubmitted: (email: string) => void
  onDismiss: () => void
}

/**
 * Offered when a handoff has happened and no human has replied within the
 * threshold glue serves on the `session` event. Optional and dismissible: the
 * visitor can ignore it and keep waiting.
 *
 * Submits through /api/chat/identify, which attaches the address to the
 * Chatwoot CONTACT (not the conversation) — that is the field an agent looks at,
 * and the one `continuity_via_email` reads if it is ever enabled.
 */
export function SlowReplyForm({
  authToken,
  conversationId,
  waitedMs,
  onSubmitted,
  onDismiss,
}: SlowReplyFormProps) {
  const t = useTranslations("chat.ui.slowReply")
  const inputId = useId()
  const errorId = useId()
  const [email, setEmail] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // Two different failures, and telling them apart matters: a missing token is
    // our problem, and reporting it as "invalid email" leaves someone who typed
    // a perfectly good address with nothing to try.
    if (!authToken) {
      setError(t("errorGeneric"))
      return
    }
    if (!emailValid) {
      setError(t("errorEmail"))
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      // Sent so glue can stamp it as the `posthog_distinct_id` custom
      // attribute on the Chatwoot contact. Omitted when PostHog has no id.
      const distinctId = getPostHogDistinctId()
      const res = await fetch("/api/chat/identify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Auth-Token": authToken,
        },
        body: JSON.stringify({
          email: email.trim(),
          conversation_id: conversationId,
          after_wait_ms: waitedMs,
          ...(distinctId ? { posthog_distinct_id: distinctId } : {}),
        }),
      })
      if (!res.ok) throw new Error(`identify failed (${res.status})`)
      onSubmitted(email.trim())
    } catch (err) {
      // We just lost a way to reach someone who was already kept waiting —
      // capture so on-call can follow up from the transcript.
      captureChatError(err, {
        conversation_id: conversationId,
        handed_off: true,
        extra: { form: "slow-reply", waited_ms: waitedMs },
      })
      setError(t("errorGeneric"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="border-deep-blue-100 bg-deep-blue-50/60 flex flex-col gap-2 rounded-2xl border p-3"
    >
      <p className="text-deep-blue-950 text-sm font-semibold">{t("title")}</p>
      <p className="text-deep-blue-900 type-body-sm">{t("subtitle")}</p>
      {/* The placeholder is an example address, not a name — and it disappears
          the moment the visitor types. A real label is what a screen reader
          announces (WCAG 4.1.2 / 3.3.2). */}
      <label htmlFor={inputId} className="sr-only">
        {t("emailLabel")}
      </label>
      <input
        id={inputId}
        type="email"
        required
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("placeholder")}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={cn(
          "text-foreground rounded-[12px] border border-neutral-200 bg-white px-3 py-2 text-base transition-colors outline-none placeholder:text-neutral-400 sm:text-sm",
          "focus:border-deep-blue-800 focus:ring-deep-blue-50 focus:ring-2"
        )}
      />
      {error && (
        <p id={errorId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting || !emailValid}
          className={cn(
            "bg-deep-blue-950 hover:bg-deep-blue-900 flex-1 rounded-full px-3 py-2 text-sm font-medium text-white transition-colors",
            "disabled:opacity-50"
          )}
        >
          {submitting ? t("submitting") : t("submit")}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-full px-3 py-2 text-sm font-medium text-neutral-500 transition-colors hover:text-neutral-700"
        >
          {t("dismiss")}
        </button>
      </div>
    </form>
  )
}
